export default customElements.define('w-progress-tracker-2', class ProgressTracker2 extends wealthify.CustomElementBaseClass {

    constructor() {
        super();
    }

    // Public

    static styleSelector = 'c-progress-tracker-2';
    
    static template = `
    <div data-root>
      <div class="${this.styleSelector}__container" data-container></div>
    </div>
    `;
    
    static stepTemplate = `
    <div class="${this.styleSelector}__step" data-step>
        <w-icon size="200" class="${this.styleSelector}__step-icon" data-step-icon></w-icon>
        <label class="${this.styleSelector}__step-title" data-step-title></label>
    </div>
    `;

    static stepInfoTemplate = `
    <div class="${this.styleSelector}__step-info" data-step-info>
      <div class="${this.styleSelector}__step-info-progress-wrapper" data-progress-wrapper>
        <progress class="${this.styleSelector}__step-info-progress-bar" min="0" max="100" data-progressbar></progress>
        <w-icon size="200" type='warning' class="${this.styleSelector}__step-info-warning-icon" data-progressbar-warning-icon></w-icon>
      </div>
      
      <p class="${this.styleSelector}__step-info-description" data-step-description></p>
    </div>
    `;

    static mobileActiveStep = `
    <div class="${this.styleSelector}__mobile-step" aria-hidden="true">
      <label data-mobile-step-label></label>
    </div>
    `;

    static requiredPropertiesForSteps = ['title', 'state'];
    static requiredPropertiesCompletedStep = [ 'state'];

    static layouts = {
        Stacked: 'stacked',
        Inline: 'inline'
    };

    static get validLayouts () {
        return Object.values(this.layouts);
    }
    
    static get observedProps () {
        return [
            new wealthify.Prop('steps', {
                type: wealthify.propTypes.Array(),
                validators: [wealthify.propValidators.ArrayObjectContainsKeys(this.requiredPropertiesForSteps)],
            }),
            new wealthify.Prop('completed-step', {
                default: {state: 'inactive'},
                type: wealthify.propTypes.Object(),
                validators: [wealthify.propValidators.ObjectContainsKeys(this.requiredPropertiesCompletedStep)]
            }),
            new wealthify.Prop('layout', {
                default: this.layouts.Inline,
                validators: [wealthify.propValidators.IsInArray(this.validLayouts)]
            })
        ];
    }

    get propCallbacks() {
        return {
            steps: () => this.#createSteps(),            
            layout: () => {
                this.constructor.validLayouts.forEach(type => {
                    if (type === this.layout) {
                        this.classList.add(`${this.styleSelector}--${type}`);
                    } else {
                        this.classList.remove(`${this.styleSelector}--${type}`);
                    }
                });
            },
            completedStep: () => {}
        };
    }
    
    // Private
    
    #states = {
        Complete: 'complete',
        Inactive: 'inactive',
        Active: 'active',
        Blocked: 'blocked'
    };
  
  #stateLabelMap = {[this.#states.Blocked]: ', contains errors.'};
  
  #stateProgressValueMap = {
      [this.#states.Complete]: 100,
      [this.#states.Active]: 50,
      [this.#states.Inactive]: 0,
      [this.#states.Blocked]: 50
  };

  #createSteps(){
      const fragment = document.createDocumentFragment();

      this.steps?.forEach((step, index) => {
          // Create step
          const stepElement = this.#createSingleStep(step);
          
          fragment.appendChild(stepElement);
          
          // Create progress bar
          const oneBasedIndex = index + 1;
          const progressBar = this.#createProgressInfo(stepElement, step, oneBasedIndex);
          
          fragment.appendChild(progressBar);
      });

      const completedStep = this.#createSingleStep(this.completedStep);
        
      fragment.appendChild(completedStep);
       
      // Show current step in isolation when viewport is limited
      // (when layout is set to inline only).
      if(this.layout === this.constructor.layouts.Inline) {
          this.#appendInlineMobileCurrentStep();
      }

      this.dom.container.appendChild(fragment);
  }

  #createSingleStep(step) {
      const stepElement = wealthify.dom.fromString(this.constructor.stepTemplate);
      const label = stepElement.querySelector('[data-step-title]');           
      const icon = stepElement.querySelector('[data-step-icon]');
      
      stepElement.classList.add(`${this.styleSelector}__step--${step.state}`);
      
      if (this.layout === this.constructor.layouts.Inline) {
          label.classList.add('u-display__hidden--mobile');
      }
      
      if(step.title) { 
          label.innerHTML = `${step.title} <span class="u-display__hidden">${this.#stateLabelMap[step.state] ? this.#stateLabelMap[step.state] : `, ${step.state}`}</span>`; 
      }
      
      icon.type = step.icon || 'success';
      
      return stepElement;
  }
  
  #createProgressInfo(stepElement, step, stepIndex) {
      const stepInfo = wealthify.dom.fromString(this.constructor.stepInfoTemplate);
      const stepTitleLabel = stepElement.querySelector('[data-step-title]');
      const warningIcon = stepInfo.querySelector('[data-progressbar-warning-icon]');
      const progressBar = stepInfo.querySelector('[data-progressbar]');
      const progressBarId = wealthify.convert.toCamelCase(`step${stepIndex}`);
      const stepDescription = stepInfo.querySelector('[data-step-description]');
      
      warningIcon.hide = step.state !== this.#states.Blocked;
      
      stepTitleLabel.htmlFor = progressBarId;
      progressBar.id = progressBarId;

      if (!this.#stateProgressValueMap[step.state] === undefined) {
          wealthify.error.throwError(this.tagName, `Step state "${step.state}" is not valid.`);
      }
      
      progressBar.value = this.#stateProgressValueMap[step.state];
      progressBar.ariaValueText = `${progressBar.value}%`;

      progressBar.setAttribute('aria-hidden', step.state !== this.#states.Active);
     
      stepDescription.textContent = this.layout === this.constructor.layouts.Stacked ? step.description : '';
      
      return stepInfo;
  }

  #appendInlineMobileCurrentStep() {
      const currentStepStates = [
          this.#states.Active,
          this.#states.Blocked,
          this.#states.Complete,
      ];

      const allSteps = [...this.steps, this.completedStep];
      const currentStep = allSteps.findLast(step => currentStepStates.includes(step.state));

      if(currentStep === undefined) { 
          return;
      }
      
      const mobileActiveStep = wealthify.dom.fromString(this.constructor.mobileActiveStep);
      const mobileActiveStepLabel = mobileActiveStep.querySelector('[data-mobile-step-label]');
        
      mobileActiveStepLabel.innerHTML = currentStep.title || '';
        
      this.dom.root.appendChild(mobileActiveStep);
  }
});